// src/components/NewVehiclePhotosTab.tsx

import React, { useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Image } from 'antd';
import {
  DownloadOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { VehicleMedia } from '../types/NewVehicleTypes';

// --- Ant Design Icons Style ---
const iconStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: 20,
  cursor: 'pointer',
  padding: '4px',
  transition: '0.2s',
};

// --- Custom Ant Design Image Toolbar ---
const CustomToolbar = ({
  onFlipY,
  onFlipX,
  onRotateLeft,
  onRotateRight,
  onZoomOut,
  onZoomIn,
  onReset,
  currentUrl,
  scale,
}: any) => {
  const handleDownload = () => {
    if (!currentUrl) return;
    const suffix = currentUrl.slice(currentUrl.lastIndexOf('.'));
    const filename = `vehicle-image-${Date.now()}${suffix}`;
    fetch(currentUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(blobUrl);
        link.remove();
      });
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 50,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0,0,0,0.6)',
        padding: '10px 20px',
        borderRadius: 30,
        zIndex: 1601,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
      }}
    >
      <DownloadOutlined style={iconStyle} onClick={handleDownload} />
      <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
      <SwapOutlined style={iconStyle} onClick={onFlipX} />
      <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
      <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
      <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
      <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
      <UndoOutlined style={iconStyle} onClick={onReset} />
    </div>
  );
};

// --- Main Component ---
interface NewVehiclePhotosTabProps {
  images: VehicleMedia[];
}

const NewVehiclePhotosTab = ({ images }: NewVehiclePhotosTabProps) => {
  const [previewIndex, setPreviewIndex] = useState(0);
  const [imageModalVisible, setImageModalVisible] = useState(false);

  // For this powerful layout, we display exactly 5 images:
  // 2 large on the top row, 3 smaller on the bottom row.
  const displayImages = images.slice(0, 5);
  const remainingCount = images.length > 5 ? images.length - 5 : 0;

  if (!images || images.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No photos available for this vehicle.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Dynamic 6-Column Gallery Layout */}
      <Box
        sx={{
          display: 'grid',
          // Desktop uses 6 columns. Mobile uses 2.
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
          gap: { xs: 1, sm: 1.5, md: 2 },
          mb: 2,
        }}
      >
        {displayImages.map((img, index) => {
          const isTopRow = index < 2; // First 2 images
          const isLastSlot = index === 4; // 5th image
          const showOverlay = isLastSlot && remainingCount > 0;

          return (
            <Box
              key={img.mediaId}
              onClick={() => {
                setPreviewIndex(index);
                setImageModalVisible(true);
              }}
              sx={{
                // Column Spanning Logic
                // Desktop: Top row spans 3 cols (50%), Bottom spans 2 cols (33%)
                // Mobile: Top row spans 2 cols (100%), Bottom spans 1 col (50%), Last image 100%
                gridColumn: isTopRow 
                  ? { xs: 'span 2', md: 'span 3' } 
                  : { xs: isLastSlot ? 'span 2' : 'span 1', md: 'span 2' },
                
                // Height Proportions (Mathematically calculated to match 16:9 so cars aren't cut off)
                height: isTopRow 
                  ? { xs: 220, sm: 300, md: 360 }  
                  : { xs: isLastSlot ? 220 : 120, sm: 180, md: 230 }, 
                
                borderRadius: 2.5, // Smooth premium corners
                overflow: 'hidden',
                position: 'relative',
                cursor: 'zoom-in',
                bgcolor: '#f5f5f5',
                
                // Group hover effect targeting the inner image
                '&:hover .gallery-image': {
                  transform: 'scale(1.05)',
                },
                '&:hover .overlay-panel': {
                  bgcolor: 'rgba(0, 0, 0, 0.65)',
                }
              }}
            >
              <Box
                className="gallery-image"
                component="img"
                src={img.url}
                alt={`Vehicle image ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center', // Ensures the focal point stays centered
                  display: 'block',
                  transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Silky smooth zoom
                }}
              />
              
              {/* Premium Glassmorphism Overlay */}
              {showOverlay && (
                <Box
                  className="overlay-panel"
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.5)', 
                    backdropFilter: 'blur(4px)', // High-end frosted glass effect
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    transition: 'background-color 0.3s',
                  }}
                >
                  <Typography variant="h3" fontWeight={800}>
                    +{remainingCount}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} mt={0.5} sx={{ letterSpacing: 1 }}>
                    View More
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer Disclaimer */}
      <Stack direction="row" alignItems="center" spacing={1} mt={3} color="text.secondary">
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          Images may show optional features and accessories.
        </Typography>
      </Stack>

      {/* --- Ant Design Hidden Lightbox --- */}
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible: imageModalVisible,
            current: previewIndex,
            onVisibleChange: (vis) => setImageModalVisible(vis),
            onChange: (index) => setPreviewIndex(index),
            toolbarRender: (originalNode, info) => (
              <CustomToolbar
                {...info.actions}
                scale={info.transform.scale}
                currentUrl={images[previewIndex]?.url}
              />
            ),
          }}
        >
          {images.map((img) => (
            <Image key={img.mediaId} src={img.url} />
          ))}
        </Image.PreviewGroup>
      </div>
    </Box>
  );
};

export default NewVehiclePhotosTab;